import { GetServerSideProps } from 'next'
import Link from 'next/link'
import { slugify } from 'utils/slug'
import api from 'services/api'
import Layout from 'components/layout'
import TitleBar from 'components/title-bar'
import routes from 'routes'
import BookDetails from 'models/book-details'
import ChapterListItem from 'models/chapter-list-item'
import { NextSeo } from 'next-seo'
import { StoryContext } from 'contexts/story'
import { useContext, useEffect, useState } from 'react'
import { getRedirectReturn } from 'utils/redirect'
import { CANONICAL_PREFIX } from 'config'
import Breadcrumb from 'components/breadcrumb'
import { getUserInfoCache } from 'utils/user-info'
import Loading from 'components/loading'
import { withStorage } from 'utils/withFunction'

const ChapterList = ({
  bookInfo,
  chapters,
  path,
}: {
  bookInfo: BookDetails
  chapters: ChapterListItem[]
  path: string
}): JSX.Element => {
  const { book } = bookInfo
  const [userProfile, setUserProfile] = useState(null)
  const [userChapters, setUserChapters] = useState(null)

  const storyContext = useContext(StoryContext)

  useEffect(() => {
    storyContext.currentStory = book

    return () => {
      storyContext.currentStory = null
    }
  })

  useEffect(() => {
    setUserProfile(getUserInfoCache())
  }, [])

  useEffect(() => {
    if (userProfile) {
      Loading.show()
      api.chapter.getUserChapterList(book.bookId).then((response) => {
        Loading.hide()
        setUserChapters(response)
      })
    }
  }, [userProfile])

  return (
    <Layout>
      <NextSeo
        title={`${bookInfo.book.title} All Chapters Read Online for Free`}
        description={`${bookInfo.book.title} by ${bookInfo.book.author.authorName} latest chapter, and all ${chapters.length} chapters.`}
        canonical={`${CANONICAL_PREFIX}${path}`}
      />
      <TitleBar
        title={`${bookInfo.book.title} all ${chapters.length} chapters`}
      />
      <div className="hidden lg:block px-4 py-3">
        <Breadcrumb
          className="mr-4"
          items={[
            {
              name: `${bookInfo.book.title}`,
              link: routes.story.details({
                id: bookInfo.book.bookId,
                title: `${bookInfo.book.title}`,
              }),
            },
            {
              name: 'all chapters',
            },
          ]}
        />
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-3 gap-x-16 p-4 divide-y divide-gray-100">
        {chapters.map((chapter, index) => {
          return (
            <li key={chapter.id} className="text-sm py-1">
              <Link
                href={routes.story.chapter({
                  bookId: book.bookId,
                  bookTitle: book.title,
                  chapterOrdinal: chapter.order,
                })}
                className="flex items-center justify-between h-12"
              >
                <div>{chapter.title}</div>
                {!chapter.isFree &&
                  !userChapters?.purchased?.some(
                    (o) => o.order === index + 1
                  ) && (
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                      />
                    </svg>
                  )}
              </Link>
            </li>
          )
        })}
      </ol>
    </Layout>
  )
}

export const getServerSideProps: GetServerSideProps = withStorage(
  async ({ context }) => {
    const id = context.params.id as string
    const slug = context.params.slug as string

    const [bookInfo, chapters] = await Promise.all([
      api.book.getBookInfo(id),
      api.chapter.getChapterList(id),
    ])

    if (!bookInfo) {
      return {
        redirect: {
          permanent: true,
          destination: `/search?keywords=${slug.replaceAll('-', '+')}`,
        },
      }
    }

    const path = routes.story.chapterList({
      id: bookInfo.book.bookId,
      title: bookInfo.book.title,
    })

    if (slugify(bookInfo.book.title) !== slug) {
      return getRedirectReturn(context.req.url, path)
    }

    return {
      props: {
        bookInfo,
        chapters,
        path,
      },
    }
  }
)

export default ChapterList
